/**
 * System architecture diagram for the Platforms page.
 *
 * Layout (left → right):
 *   Phone  ←→  ISD Server  ──→  Backend stack (MCP / API / Data / LLM)
 *                  │
 *                  ↓
 *           Disposable Dashboards · Documents  (sent back to phone)
 */
export default function SystemArchitecture() {
  return (
    <section
      data-testid="system-architecture-section"
      className="relative py-24 md:py-32 border-t border-white/10 bg-[#080808]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            Under the hood
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
          <h2 className="lg:col-span-8 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-snug">
            One message in.
            <br />
            <span className="text-zinc-500">
              A whole stack quietly works for you.
            </span>
          </h2>
          <p className="lg:col-span-4 text-base leading-relaxed text-zinc-400 font-light">
            Every iMessage thread is the visible surface of a real engineered
            system — server, data, tools, models, and human oversight.
          </p>
        </div>

        <div className="border border-white/10 bg-[#050505] p-4 sm:p-8 lg:p-12 overflow-x-auto">
          <svg
            viewBox="0 0 1100 720"
            className="w-full h-auto block min-w-[760px]"
            role="img"
            aria-label="System architecture diagram"
          >
            <defs>
              <marker
                id="arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#FACC15" />
              </marker>
              <marker
                id="arrow-dim"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(250,204,21,0.5)" />
              </marker>
            </defs>

            {/* ───────────── PHONE ───────────── */}
            <g>
              <rect x="40" y="120" width="150" height="220" rx="22" fill="#0a0a0a" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2" />
              <rect x="48" y="138" width="134" height="184" rx="14" fill="#070707" stroke="rgba(255,255,255,0.06)" strokeWidth="0.8" />
              <rect x="88" y="128" width="54" height="6" rx="3" fill="rgba(255,255,255,0.18)" />
              {/* chat bubble */}
              <rect x="60" y="156" width="86" height="22" rx="11" fill="#1e88e5" />
              <rect x="60" y="184" width="68" height="22" rx="11" fill="#1e88e5" opacity="0.6" />
              <rect x="86" y="212" width="86" height="22" rx="11" fill="rgba(255,255,255,0.10)" />
              <text x="115" y="305" textAnchor="middle" fontFamily="Outfit, sans-serif" fontSize="13" fill="white" fontWeight="500" letterSpacing="0.5">iMessage</text>
              <text x="115" y="370" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="rgba(255,255,255,0.4)" letterSpacing="2">CUSTOMER</text>
            </g>

            {/* ─────── PHONE ↔ SERVER ─────── */}
            {/* outgoing (top) */}
            <line x1="200" y1="190" x2="395" y2="190" stroke="#FACC15" strokeWidth="1.4" markerEnd="url(#arrow)" />
            <text x="297" y="180" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#FACC15" letterSpacing="2">REQUEST</text>
            {/* return (bottom) */}
            <line x1="395" y1="270" x2="200" y2="270" stroke="#FACC15" strokeWidth="1.4" markerEnd="url(#arrow)" />
            <text x="297" y="290" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#FACC15" letterSpacing="2">REPLY</text>

            {/* ───────────── ISD SERVER ───────────── */}
            <g>
              <rect x="395" y="120" width="220" height="220" rx="4" fill="#0a0a0a" stroke="rgba(250,204,21,0.55)" strokeWidth="1.4" />
              {/* server rack lines */}
              {[150, 170, 190, 210, 230, 250].map((y) => (
                <g key={y}>
                  <rect x="415" y={y} width="180" height="12" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="0.8" />
                  <circle cx="425" cy={y + 6} r="1.6" fill="#FACC15" />
                </g>
              ))}
              <text x="505" y="305" textAnchor="middle" fontFamily="Outfit, sans-serif" fontSize="14" fill="white" fontWeight="500" letterSpacing="0.5">ISD Server</text>
              <text x="505" y="325" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="rgba(250,204,21,0.85)" letterSpacing="2">HUMAN-IN-THE-LOOP</text>
            </g>

            {/* ─────── SERVER → BACKEND STACK ─────── */}
            {/* MCP */}
            <line x1="615" y1="160" x2="745" y2="120" stroke="rgba(250,204,21,0.6)" strokeWidth="1.2" markerEnd="url(#arrow-dim)" />
            <g>
              <rect x="745" y="100" width="320" height="60" rx="3" fill="#0a0a0a" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
              <g transform="translate(770, 122)">
                {/* MCP icon — abstract plug */}
                <circle cx="0" cy="8" r="9" fill="none" stroke="#FACC15" strokeWidth="1.4" />
                <line x1="-9" y1="8" x2="-16" y2="8" stroke="#FACC15" strokeWidth="1.4" />
                <line x1="9" y1="8" x2="16" y2="8" stroke="#FACC15" strokeWidth="1.4" />
                <line x1="0" y1="-1" x2="0" y2="-8" stroke="#FACC15" strokeWidth="1.4" />
              </g>
              <text x="810" y="125" fontFamily="Outfit, sans-serif" fontSize="14" fill="white" fontWeight="500">MCP</text>
              <text x="810" y="143" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="rgba(255,255,255,0.4)" letterSpacing="1.4">tool-use protocol</text>
            </g>

            {/* API */}
            <line x1="615" y1="195" x2="745" y2="200" stroke="rgba(250,204,21,0.6)" strokeWidth="1.2" markerEnd="url(#arrow-dim)" />
            <g>
              <rect x="745" y="180" width="320" height="60" rx="3" fill="#0a0a0a" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
              <g transform="translate(770, 200)">
                {/* API — braces + arrow */}
                <text x="-6" y="6" fontFamily="JetBrains Mono, monospace" fontSize="20" fill="#FACC15">{"{ }"}</text>
              </g>
              <text x="810" y="205" fontFamily="Outfit, sans-serif" fontSize="14" fill="white" fontWeight="500">API</text>
              <text x="810" y="223" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.4)" letterSpacing="0.6">webhooks · REST · GraphQL</text>
            </g>

            {/* DATA LAYER */}
            <line x1="615" y1="230" x2="745" y2="280" stroke="rgba(250,204,21,0.6)" strokeWidth="1.2" markerEnd="url(#arrow-dim)" />
            <g>
              <rect x="745" y="260" width="320" height="60" rx="3" fill="#0a0a0a" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
              <g transform="translate(770, 280)">
                {/* DB cylinder */}
                <ellipse cx="0" cy="-3" rx="9" ry="3" fill="none" stroke="#FACC15" strokeWidth="1.3" />
                <line x1="-9" y1="-3" x2="-9" y2="6" stroke="#FACC15" strokeWidth="1.3" />
                <line x1="9" y1="-3" x2="9" y2="6" stroke="#FACC15" strokeWidth="1.3" />
                <ellipse cx="0" cy="6" rx="9" ry="3" fill="none" stroke="#FACC15" strokeWidth="1.3" />
                <ellipse cx="0" cy="1" rx="9" ry="3" fill="none" stroke="rgba(250,204,21,0.4)" strokeWidth="1" />
              </g>
              <text x="810" y="285" fontFamily="Outfit, sans-serif" fontSize="14" fill="white" fontWeight="500">Data Layer</text>
              <text x="810" y="303" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.4)" letterSpacing="0.6">RAG · Postgres · pgvector · S3</text>
            </g>

            {/* LLM */}
            <line x1="615" y1="270" x2="745" y2="360" stroke="rgba(250,204,21,0.6)" strokeWidth="1.2" markerEnd="url(#arrow-dim)" />
            <g>
              <rect x="745" y="340" width="320" height="80" rx="3" fill="#0a0a0a" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
              <text x="765" y="365" fontFamily="Outfit, sans-serif" fontSize="14" fill="white" fontWeight="500">LLM</text>
              <text x="765" y="383" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.4)" letterSpacing="0.6">multi-provider routing</text>
              {/* Brand logos overlay */}
              <image href="/logos/gemini.png" x="930" y="350" width="32" height="32" preserveAspectRatio="xMidYMid meet" />
              <image href="/logos/claude.png" x="970" y="350" width="32" height="32" preserveAspectRatio="xMidYMid meet" />
              <image href="/logos/openai.png" x="1010" y="350" width="32" height="32" preserveAspectRatio="xMidYMid meet" />
            </g>

            {/* ─────── SERVER → DASHBOARDS / DOCS (sent back to phone) ─────── */}
            <line x1="505" y1="340" x2="505" y2="450" stroke="#FACC15" strokeWidth="1.4" strokeDasharray="4 4" markerEnd="url(#arrow)" />
            <text x="520" y="395" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#FACC15" letterSpacing="2">GENERATES</text>

            {/* Dashboards card */}
            <g>
              <rect x="200" y="450" width="280" height="100" rx="3" fill="#0a0a0a" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
              <g transform="translate(220, 470)">
                {/* mini dashboard glyph */}
                <rect x="0" y="0" width="36" height="28" rx="2" fill="none" stroke="#FACC15" strokeWidth="1.2" />
                <line x1="4" y1="6" x2="20" y2="6" stroke="#FACC15" strokeWidth="1" />
                <rect x="4" y="11" width="6" height="13" fill="#FACC15" opacity="0.55" />
                <rect x="14" y="14" width="6" height="10" fill="#FACC15" opacity="0.8" />
                <rect x="24" y="9" width="6" height="15" fill="#FACC15" />
              </g>
              <text x="270" y="486" fontFamily="Outfit, sans-serif" fontSize="14" fill="white" fontWeight="500">Disposable Dashboards</text>
              <text x="270" y="504" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.4)" letterSpacing="0.6">one-link · expires when done</text>
            </g>

            {/* Documents card */}
            <g>
              <rect x="510" y="450" width="280" height="100" rx="3" fill="#0a0a0a" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
              <g transform="translate(530, 470)">
                {/* doc glyph */}
                <path d="M 0 0 L 22 0 L 30 8 L 30 28 L 0 28 Z" fill="none" stroke="#FACC15" strokeWidth="1.2" strokeLinejoin="round" />
                <path d="M 22 0 L 22 8 L 30 8" fill="none" stroke="#FACC15" strokeWidth="1.2" strokeLinejoin="round" />
                <line x1="6" y1="14" x2="24" y2="14" stroke="#FACC15" strokeWidth="0.9" />
                <line x1="6" y1="18" x2="24" y2="18" stroke="#FACC15" strokeWidth="0.9" />
                <line x1="6" y1="22" x2="18" y2="22" stroke="#FACC15" strokeWidth="0.9" />
              </g>
              <text x="580" y="486" fontFamily="Outfit, sans-serif" fontSize="14" fill="white" fontWeight="500">Documents</text>
              <text x="580" y="504" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.4)" letterSpacing="0.6">PDF · contract · invoice · receipt</text>
            </g>

            {/* Arrows from dashboards/docs back to phone */}
            <path d="M 200 500 Q 140 500 115 415 L 115 350" fill="none" stroke="#FACC15" strokeWidth="1.4" strokeDasharray="4 4" markerEnd="url(#arrow)" />
            <text x="38" y="475" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#FACC15" letterSpacing="2">DELIVERED</text>

            {/* Bottom signal strip */}
            <line x1="40" y1="640" x2="1060" y2="640" stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
            <text x="40" y="670" fontFamily="JetBrains Mono, monospace" fontSize="11" fill="rgba(255,255,255,0.5)" letterSpacing="2">
              MESSAGE · SERVER · TOOLS · MODEL · REPLY
            </text>
            <text x="1060" y="670" textAnchor="end" fontFamily="JetBrains Mono, monospace" fontSize="11" fill="rgba(250,204,21,0.7)" letterSpacing="2">
              p50 142 ms · p99 390 ms
            </text>
          </svg>
        </div>

        {/* Caption row */}
        <div className="mt-8 grid sm:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {[
            { k: "Disposable", v: "Dashboards & docs that auto-expire when the job is done." },
            { k: "Composable", v: "MCP-driven tool use — swap or add capabilities without re-platforming." },
            { k: "Grounded", v: "RAG-backed responses on your own data, never just vibes." },
            { k: "Multi-model", v: "Routed across Gemini, Claude, and OpenAI per task." },
          ].map((c) => (
            <div key={c.k} className="bg-[#050505] p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-yellow-400 mb-2">{c.k}</div>
              <div className="text-sm text-zinc-400 font-light leading-relaxed">{c.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
